import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

// ─── Thunks ───────────────────────────────────────────────

export const fetchMyQuota = createAsyncThunk(
  'subscription/fetchMyQuota',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('subscriptions/my-quota');
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load quota');
    }
  }
);

export const fetchAllPlans = createAsyncThunk(
  'subscription/fetchAllPlans',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('subscriptions/plans');
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load plans');
    }
  }
);

export const createPlan = createAsyncThunk(
  'subscription/createPlan',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('subscriptions/plans', data);
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create plan');
    }
  }
);

export const updatePlan = createAsyncThunk(
  'subscription/updatePlan',
  async ({ planId, data }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`subscriptions/plans/${planId}`, data);
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update plan');
    }
  }
);

export const setPlanActive = createAsyncThunk(
  'subscription/setPlanActive',
  async ({ planId, active }, { rejectWithValue }) => {
    try {
      await axiosInstance.patch(`subscriptions/plans/${planId}/active?active=${active}`);
      return { planId, active };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update plan status');
    }
  }
);

export const fetchAllCompanySubscriptions = createAsyncThunk(
  'subscription/fetchAllCompanySubscriptions',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('subscriptions/companies');
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load subscriptions');
    }
  }
);

export const assignSubscription = createAsyncThunk(
  'subscription/assignSubscription',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('subscriptions/companies/assign', data);
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to assign subscription');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState: {
    myQuota: null,
    plans: [],
    companySubscriptions: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearSubscriptionError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // My Quota
      .addCase(fetchMyQuota.fulfilled, (state, action) => {
        state.myQuota = action.payload;
      })
      // All Plans
      .addCase(fetchAllPlans.pending, (state) => { state.loading = true; })
      .addCase(fetchAllPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload || [];
      })
      .addCase(fetchAllPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Plan
      .addCase(createPlan.fulfilled, (state, action) => {
        if (action.payload) state.plans.push(action.payload);
      })
      // Update Plan
      .addCase(updatePlan.fulfilled, (state, action) => {
        const idx = state.plans.findIndex(p => p.planId === action.payload?.planId);
        if (idx !== -1) state.plans[idx] = action.payload;
      })
      // Toggle Plan Active
      .addCase(setPlanActive.fulfilled, (state, action) => {
        const plan = state.plans.find(p => p.planId === action.payload.planId);
        if (plan) plan.isActive = action.payload.active;
      })
      // All Company Subscriptions
      .addCase(fetchAllCompanySubscriptions.pending, (state) => { state.loading = true; })
      .addCase(fetchAllCompanySubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.companySubscriptions = action.payload || [];
      })
      .addCase(fetchAllCompanySubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Assign Subscription
      .addCase(assignSubscription.fulfilled, (state, action) => {
        if (!action.payload) return;
        const idx = state.companySubscriptions.findIndex(s => s.companyId === action.payload.companyId);
        if (idx !== -1) state.companySubscriptions[idx] = action.payload;
        else state.companySubscriptions.push(action.payload);
      });
  },
});

export const { clearSubscriptionError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;